import { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { CustomColumnDef } from "Common/Tables/ObjectFiltersTable/DashboardTableContainer";
import { api } from "helpers/api_url";
import { ArrowRight, MousePointerClick, ShoppingCart } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Card, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { fetchDailyOrderTrend, fetchOrderList, fetchOrderSummaryStatistics } from "slices/Dashboard/Order_Summary/thunk";
import { WarehouseOption } from "./DashboardFilters";
import { DashboardSlidePanel } from "./Reusable/DashboardSlidePanel";
import "./OrderSummary.css";

interface OrderSummaryProps {
  dateFrom?: Date;
  dateTo?: Date;
  warehouse?: WarehouseOption[];
}

interface OrderListingItem {
  order_id: string;
  order_number: string;
  order_date: string;
  customer_name: string;
  contact_person_name: string;
  contact_person_phone_no: string;
  expected_delivery_date: string | null;
  total_amount: string;
  status: string;
  source: 'from_quotation' | 'direct';
  quotation_id: string | null;
  quotation_number: string | null;
  created_at: string;
  updated_at: string;
  created_by_username: string | null;
  updated_by_username: string | null;
}

export function OrderSummary({ dateFrom, dateTo, warehouse = [] }: OrderSummaryProps) {
  const dispatch = useDispatch<any>();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'fromQuotation' | 'direct'>('all');

  // Table state
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [filterValue, setFilterValue] = useState<any[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  // Get data from Redux store
  const { statistics, statisticsLoading, statisticsError, orderList, totalRecords, listLoading, listError, dailyOrderTrend, dailyOrderTrendLoading, dailyOrderTrendError } = useSelector(
    (state: any) => state.OrderSummaryReducer
  );

  // Fetch order summary statistics
  useEffect(() => {
    dispatch(
      fetchOrderSummaryStatistics({
        dateFrom,
        dateTo,
        warehouse,
      })
    ).catch((error: any) => {
      console.error("Error fetching order summary statistics:", error);
      toast.error("Failed to load order summary data");
    });
  }, [dispatch, dateFrom, dateTo, warehouse]);

  // Fetch daily order trend data
  useEffect(() => {
    dispatch(
      fetchDailyOrderTrend({
        dateFrom,
        dateTo,
        warehouse,
      })
    ).catch((error: any) => {
      console.error("Error fetching daily order trend:", error);
      toast.error("Failed to load daily order trend data");
    });
  }, [dispatch, dateFrom, dateTo, warehouse]);

  const totalOrders = statistics?.total_orders ?? 0;
  const fromQuotation = statistics?.from_quotation ?? 0;
  const directOrders = statistics?.direct_orders ?? 0;
  const confirmed = statistics?.confirmed ?? 0;
  const processing = statistics?.processing ?? 0;
  const completed = statistics?.completed ?? 0;
  const cancelled = statistics?.cancelled ?? 0;
  const fromQuotationCompleted = statistics?.from_quotation_completed ?? 0;
  const fromQuotationCancelled = statistics?.from_quotation_cancelled ?? 0;
  const fromQuotationProcessing = statistics?.from_quotation_processing ?? 0;
  const directCompleted = statistics?.direct_completed ?? 0;
  const directCancelled = statistics?.direct_cancelled ?? 0;
  const directProcessing = statistics?.direct_processing ?? 0;

  const successRate = totalOrders > 0 ? Math.round((completed / totalOrders) * 100) : 0;

  const handleCardClick = (filter: 'all' | 'fromQuotation' | 'direct') => {
    setSelectedFilter(filter);
    setIsPanelOpen(true);
    setPageIndex(0); // Reset to first page when opening panel
  };

  // Convert selectedFilter to API filter_type
  const filterType = useMemo((): 'all' | 'from_quotation' | 'direct' => {
    if (selectedFilter === 'fromQuotation') return 'from_quotation';
    if (selectedFilter === 'direct') return 'direct';
    return 'all';
  }, [selectedFilter]);

  // Build all filters including column filters
  const allFilters = useMemo(() => {
    const filters = [...filterValue];
    return filters;
  }, [filterValue]);

  // Fetch order list data
  useEffect(() => {
    if (!isPanelOpen) return;

    dispatch(
      fetchOrderList({
        page: pageIndex + 1,
        pageSize,
        filterType,
        dateFrom,
        dateTo,
        warehouse,
        sorting,
        filters: allFilters,
      })
    ).catch((error: any) => {
      console.error("Error fetching order list:", error);
      toast.error("Failed to load order list");
    });
  }, [dispatch, isPanelOpen, pageIndex, pageSize, sorting, allFilters, filterType, dateFrom, dateTo, warehouse]);

  // Reset page when filters change
  useEffect(() => {
    if (isPanelOpen) {
      setPageIndex(0);
    }
  }, [sorting, pageSize, columnFilters, selectedFilter, dateFrom, dateTo, warehouse, isPanelOpen]);

  const getOrderStatusBadge = (status: string) => {
    const statusConfig: Record<string, { className: string; label: string }> = {
      'requested for dispatch': { className: 'order-summary-badge order-summary-badge-confirmed', label: 'Requested for Dispatch' },
      'processing': { className: 'order-summary-badge order-summary-badge-processing', label: 'Processing' },
      'dispatched': { className: 'order-summary-badge order-summary-badge-completed', label: 'Dispatched' },
      'cancelled': { className: 'order-summary-badge order-summary-badge-cancelled', label: 'Cancelled' },
    };
    const config = statusConfig[status?.toLowerCase()?.trim()] || statusConfig['dispatched'];
    return <span className={config?.className}>{config?.label}</span>;
  };

  const getPanelTitle = () => {
    if (selectedFilter === 'fromQuotation') return 'Orders From Quotation';
    if (selectedFilter === 'direct') return 'Direct Orders';
    return 'All Sales Orders';
  };

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  // Format amount for display
  const formatAmount = (amount: string | number) => {
    if (!amount) return '-';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return amount;
    return `₹${numAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  // Prepare chart data
  const orderSourceData = useMemo(() => {
    if (!statistics) return [];
    return [
      { source: "Quotation", count: fromQuotation, percentage: totalOrders > 0 ? Math.round((fromQuotation / totalOrders) * 100) : 0 },
      { source: "Direct", count: directOrders, percentage: totalOrders > 0 ? Math.round((directOrders / totalOrders) * 100) : 0 },
    ];
  }, [statistics, fromQuotation, directOrders, totalOrders]);

  // Prepare daily order trend data from API
  const dailyOrderData = useMemo(() => {
    if (!dailyOrderTrend || dailyOrderTrend.length === 0) {
      return [];
    }
    
    // Transform API data to match chart format
    // API returns: [{ day: "Thu", orders: 3 }, ...]
    // Chart expects: [{ day: "Thu, 1 Jan", orders: 3 }, ...]
    const today = new Date();
    const days: Array<{ day: string; orders: number }> = [];
    
    // Create array of last 7 days in chronological order
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      const dayNumber = date.getDate();
      const monthName = date.toLocaleDateString('en-US', { month: 'short' });
      
      // Find matching data from API
      const apiData = dailyOrderTrend.find((item: { day: string; orders: number }) => item.day === dayName);
      
      days.push({
        day: `${dayName}, ${dayNumber} ${monthName}`,
        orders: apiData ? apiData.orders : 0,
      });
    }
    
    return days;
  }, [dailyOrderTrend]);

  console.log("filterType Order Data:", filterType);
  // Define columns for the table
  const columns = useMemo<CustomColumnDef<OrderListingItem>[]>(
    () => [
      {
        header: "Order No.",
        accessorKey: "order_no",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api?.dashboad?.order_list_dropdown}/order_no?order_type=${filterType}`,
        cell: ({ row }: { row: any }) => {
          return (
            <span className="order-summary-table-styles">
              {row.original?.order_no || '-'}
            </span>
          );
        },
      },
      {
        header: "Order Date",
        accessorKey: "order_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          return (
            <span className="order-summary-table-styles">
              {formatDate(row.original.order_date)}
            </span>
          );
        },
      },
      {
        header: "Customer Name",
        accessorKey: "customer_name",
        enableColumnFilter: true,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api?.dashboad?.order_list_dropdown}/customer_name?order_type=${filterType}`,
        cell: ({ row }: { row: any }) => {
          return (
            <span className="order-summary-table-styles">
              {row.original?.customer_details?.customer_name || '-'}
            </span>
          );
        },
      },
      {
        header: "Contact Person",
        accessorKey: "contact_person_name",
        enableColumnFilter: true,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api?.dashboad?.order_list_dropdown}/contact_person_name?order_type=${filterType}`,
        cell: ({ row }: { row: any }) => {
          return (
            <span className="order-summary-table-styles">
              {row.original?.customer_details?.contact_person_name || '-'}
            </span>
          );
        },
      },
      {
        header: "Phone",
        accessorKey: "contact_number",
        enableColumnFilter: true,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api?.dashboad?.order_list_dropdown}/contact_number?order_type=${filterType}`,
        cell: ({ row }: { row: any }) => {
          return (
            <span className="order-summary-table-styles">
              {row.original?.customer_details?.contact_number || '-'}
            </span>
          );
        },
      },
      // {
      //   header: "Expected Delivery",
      //   accessorKey: "expected_delivery_date",
      //   enableColumnFilter: true,
      //   enableSorting: true,
      //   filterType: "date" as const,
      //   cell: ({ row }: { row: any }) => {
      //     return (
      //       <span className="order-summary-table-date">
      //         {formatDate(row.original.expected_delivery_date)}
      //       </span>
      //     );
      //   },
      // },
      {
        header: "Amount",
        accessorKey: "total_amount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          return (
            <span className="order-summary-table-styles">
              {formatAmount(row.original.total_amount)}
            </span>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "order_status",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api?.dashboad?.order_list_dropdown}/order_status`,
        cell: ({ row }: { row: any }) => {
          return getOrderStatusBadge(row.original.order_status);
        },
      },
      {
        header: "Source",
        accessorKey: "source",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api?.dashboad?.order_list_dropdown}/source`,
        cell: ({ row }: { row: any }) => {
          return (
            <span className={row.original.quotation_id !== null ? 'order-summary-badge order-summary-badge-quotation' : 'order-summary-badge order-summary-badge-direct'}>
              {row.original.quotation_id !== null ? 'From Quotation' : 'Direct'}
            </span>
          );
        },
      },
      // {
      //   header: "Quotation Ref",
      //   accessorKey: "quotation_number",
      //   enableColumnFilter: true,
      //   enableSorting: false,
      //   filterType: "dropdown" as const,
      //   filterFetchUrl: `${api?.dashboad?.order_list_dropdown}/quotation_number`,
      //   cell: ({ row }: { row: any }) => {
      //     return (
      //       <span className="order-summary-table-date">
      //         {row.original.quotation_number || '-'}
      //       </span>
      //     );
      //   },
      // },
    ],
    [filterType]
  );

  return (
    <Card className="inventory-summary-card shadow-sm border-0 p-4">
      <div className="order-summary-header">
        <div className="order-summary-title-container">
          <ShoppingCart className="order-summary-icon" />
          <h3 className="order-summary-title">Sales Order Analytics</h3>
        </div>
        {/* <div className="order-summary-success-rate">
          <TrendingUp className="order-summary-success-rate-icon" />
          <span className="order-summary-success-rate-text">{successRate}% Completion Rate</span>
        </div> */}
      </div>

      {/* Clickable Summary Cards */}
      <div className="order-summary-cards-grid">
        <div
          onClick={() => !statisticsLoading && handleCardClick('all')}
          className={`order-summary-card order-summary-card-green ${statisticsLoading ? 'order-summary-card-loading' : ''}`}
        >
          <div className="order-summary-card-arrow">
            <ArrowRight className="order-summary-card-hint-icon" style={{ color: '#16a34a' }} />
          </div>
          <p className="order-summary-card-label order-summary-card-label-green">Total Orders</p>
          {statisticsLoading ? (
            <div className="d-flex align-items-center justify-content-center order-summary-card-value" style={{ minHeight: '2.5rem' }}>
              <Spinner animation="border" variant="success" size="sm" />
            </div>
          ) : (
            <p className="order-summary-card-value">{totalOrders}</p>
          )}

          {/* Status Breakdown */}
          {!statisticsLoading && (
            <div className="order-summary-card-status-breakdown order-summary-card-status-breakdown-green">
              <div className="order-summary-card-status-item">
                <p className="order-summary-card-status-label">Completed</p>
                <p className="order-summary-card-status-value order-summary-card-status-value-completed">{completed}</p>
              </div>
               <div className="order-summary-card-status-item">
                <p className="order-summary-card-status-label">Pending</p>
                <p className="order-summary-card-status-value order-summary-card-status-value-processing">{processing}</p>
              </div>
              <div className="order-summary-card-status-item">
                <p className="order-summary-card-status-label">Cancelled</p>
                <p className="order-summary-card-status-value order-summary-card-status-value-cancelled">{cancelled}</p>
              </div>
             
            </div>
          )}

          <div className="order-summary-card-hint order-summary-card-hint-green">
            <MousePointerClick className="order-summary-card-hint-icon" />
            <span>Click to view all</span>
          </div>
        </div>

        <div
          onClick={() => !statisticsLoading && handleCardClick('fromQuotation')}
          className={`order-summary-card order-summary-card-blue ${statisticsLoading ? 'order-summary-card-loading' : ''}`}
        >
          <div className="order-summary-card-arrow">
            <ArrowRight className="order-summary-card-hint-icon" style={{ color: '#2563eb' }} />
          </div>
          <p className="order-summary-card-label order-summary-card-label-blue">From Quotation</p>
          {statisticsLoading ? (
            <div className="d-flex align-items-center justify-content-center order-summary-card-value" style={{ minHeight: '2.5rem' }}>
              <Spinner animation="border" variant="primary" size="sm" />
            </div>
          ) : (
            <p className="order-summary-card-value">{fromQuotation}</p>
          )}

          {/* Status Breakdown */}
          {!statisticsLoading && (
            <div className="order-summary-card-status-breakdown order-summary-card-status-breakdown-blue">
              <div className="order-summary-card-status-item">
                <p className="order-summary-card-status-label">Completed</p>
                <p className="order-summary-card-status-value order-summary-card-status-value-completed">{fromQuotationCompleted}</p>
              </div>
              <div className="order-summary-card-status-item">
                <p className="order-summary-card-status-label">Pending</p>
                <p className="order-summary-card-status-value order-summary-card-status-value-processing">{fromQuotationProcessing}</p>
              </div>
              <div className="order-summary-card-status-item">
                <p className="order-summary-card-status-label">Cancelled</p>
                <p className="order-summary-card-status-value order-summary-card-status-value-cancelled">{fromQuotationCancelled}</p>
              </div>
            </div>
          )}

          <div className="order-summary-card-hint order-summary-card-hint-blue">
            <MousePointerClick className="order-summary-card-hint-icon" />
            <span>Click to view all</span>
          </div>
        </div>

        <div
          onClick={() => !statisticsLoading && handleCardClick('direct')}
          className={`order-summary-card order-summary-card-purple ${statisticsLoading ? 'order-summary-card-loading' : ''}`}
        >
          <div className="order-summary-card-arrow">
            <ArrowRight className="order-summary-card-hint-icon" style={{ color: '#9333ea' }} />
          </div>
          <p className="order-summary-card-label order-summary-card-label-purple">Direct Orders</p>
          {statisticsLoading ? (
            <div className="d-flex align-items-center justify-content-center order-summary-card-value" style={{ minHeight: '2.5rem' }}>
              <Spinner animation="border" variant="secondary" size="sm" />
            </div>
          ) : (
            <p className="order-summary-card-value">{directOrders}</p>
          )}

          {/* Status Breakdown */}
          {!statisticsLoading && (
            <div className="order-summary-card-status-breakdown order-summary-card-status-breakdown-purple">
              <div className="order-summary-card-status-item">
                <p className="order-summary-card-status-label">Completed</p>
                <p className="order-summary-card-status-value order-summary-card-status-value-completed">{directCompleted}</p>
              </div>
              <div className="order-summary-card-status-item">
                <p className="order-summary-card-status-label">Pending</p>
                <p className="order-summary-card-status-value order-summary-card-status-value-processing">{directProcessing}</p>
              </div>
              <div className="order-summary-card-status-item">
                <p className="order-summary-card-status-label">Cancelled</p>
                <p className="order-summary-card-status-value order-summary-card-status-value-cancelled">{directCancelled}</p>
              </div>
            </div>
          )}

          <div className="order-summary-card-hint order-summary-card-hint-purple">
            <MousePointerClick className="order-summary-card-hint-icon" />
            <span>Click to view all</span>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      {!statisticsLoading && orderSourceData.length > 0 && (
        <div className="order-summary-charts-grid">
          {/* Order Source Distribution Chart */}
          {/* <div className="order-summary-chart-container">
            <h4 className="order-summary-chart-title">Order Source Distribution</h4>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={orderSourceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="source"
                  tick={{ fontSize: 12 }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-white p-3 border rounded shadow" style={{ borderColor: '#e5e7eb' }}>
                          <p className="small mb-1">{payload[0].payload.source}</p>
                          <p className="small text-muted">Orders: {payload[0].value}</p>
                          <p className="small text-muted">Share: {payload[0].payload.percentage}%</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar
                  dataKey="count"
                  fill="#3b82f6"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div> */}

          {/* Daily Order Trend Chart */}
          <div className="order-summary-chart-container">
            <h4 className="order-summary-chart-title">Daily Order Trend (Last 7 Days)</h4>
            {dailyOrderTrendLoading ? (
              <div className="d-flex align-items-center justify-content-center" style={{ height: '180px' }}>
                <Spinner animation="border" variant="success" size="sm" />
              </div>
            ) : dailyOrderTrendError ? (
              <div className="d-flex align-items-center justify-content-center" style={{ height: '180px' }}>
                <p className="text-muted small">Failed to load trend data</p>
              </div>
            ) : dailyOrderData.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={dailyOrderData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-white p-3 border rounded shadow" style={{ borderColor: '#e5e7eb' }}>
                            <p className="small mb-1">{payload[0].payload.day}</p>
                            <p className="small text-muted">Orders: {payload[0].value}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="orders"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ fill: "#10b981", r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="d-flex align-items-center justify-content-center" style={{ height: '180px' }}>
                <p className="text-muted small">No data available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Order Status Summary - Compact View */}
      {/* {!statisticsLoading && (
        <div className="order-summary-status-grid">
         
          <div className="order-summary-status-item order-summary-status-item-green">
            <div className="order-summary-status-item-content">
              <Package className="order-summary-status-item-icon order-summary-status-item-icon-green" />
              <span className="order-summary-status-item-label">Completed</span>
            </div>
            <span className="order-summary-status-item-value">{completed}</span>
          </div>

          <div className="order-summary-status-item order-summary-status-item-red">
            <div className="order-summary-status-item-content">
              <XCircle className="order-summary-status-item-icon order-summary-status-item-icon-red" />
              <span className="order-summary-status-item-label">Cancelled</span>
            </div>
            <span className="order-summary-status-item-value">{cancelled}</span>
          </div>
          <div className="order-summary-status-item order-summary-status-item-orange">
            <div className="order-summary-status-item-content">
              <Clock className="order-summary-status-item-icon order-summary-status-item-icon-orange" />
              <span className="order-summary-status-item-label">Processing</span>
            </div>
            <span className="order-summary-status-item-value">{processing}</span>
          </div>
        </div>
      )} */}

      {/* Slide-out Panel */}
      <DashboardSlidePanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        icon={ShoppingCart}
        title={getPanelTitle()}
        subtitle={`${totalRecords} orders found`}
        loading={listLoading}
        columns={columns}
        data={orderList}
        totalRecords={totalRecords}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        colValue={filterValue}
        setFilterValue={setFilterValue}
        sorting={sorting}
        onSortingChange={setSorting}
        searchPlaceholder="Search orders..."
      />
    </Card>
  );
}
